import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Presentation, Users, Target, DollarSign, BookOpen, Calculator } from 'lucide-react';
import SectionTitle from './text_components/SectionTitle';
import { motion, AnimatePresence } from 'framer-motion';

const EducationalEvents = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const resources = [
    {
      title: 'Sample Educational Event Presentations',
      icon: <Users className="w-6 h-6" />,
      category: 'Presentations',
      content: [
        { name: 'Powerpoint Template', link: "https://docs.google.com/presentation/d/1MUArsbuPBtG6BPl0FfhJulwmrP-J4Xga/edit?usp=sharing&ouid=111485210408989043988&rtpof=true&sd=true" }
      ]
    },
    {
      title: 'Common Concerns for Age Groups',
      icon: <Presentation className="w-6 h-6" />,
      category: 'Age-Specific Resources',
      content: [
        { name: '20-30 Year Olds', link: 'https://drive.google.com/file/d/1HgAdwRUs_VFxCe3gu0_kGCZbMW203utt/view?usp=drive_link' },
        { name: '30-40 Year Olds', link: 'https://drive.google.com/file/d/1nwppn1rTpN_B1n5iQr0oWLDji_zuMUqY/view?usp=drive_link' },
        { name: '40-50 Year Olds', link: 'https://drive.google.com/file/d/1eeK3imPoMZXX_I4Nvimzfnu3bdamkeBx/view?usp=drive_link' },
        { name: '50-60 Year Olds', link: 'https://drive.google.com/file/d/1I0032dff_utGVpdNf7fwk80yBe2HwYn-/view?usp=drive_link' },
        { name: '60+ Year Olds', link: 'https://drive.google.com/file/d/1ysZl3fgvLfRmCeOHL9fR_ajg03Rg_uo5/view?usp=drive_link' },
      ]
    },
    {
      title: 'Traditional vs. Roth IRA',
      icon: <Target className="w-6 h-6" />,
      category: 'Retirement Accounts',
      content: [
        { name: 'Roth IRA vs. Traditional IRA - Charles Schwab', link: 'https://www.schwab.com/ira/roth-vs-traditional-ira' },
        { name: 'Roth vs. Traditional IRA Calculator - Charles Schwab', link: 'https://www.schwab.com/ira/ira-calculators/roth-vs-traditional' },
      ]
    },
    {
      title: 'Financial Literacy Resources',
      icon: <DollarSign className="w-6 h-6" />,
      category: 'Financial Education',
      content: [
        { name: 'What is Financial Literacy? - Financial Literacy 101', link: 'https://financialliteracy101.org/financial-literacy/index.cfm?' },
        { name: 'Investing Basics: FAQs - Charles Schwab', link: 'https://www.schwab.com/how-to-invest/investing-basics' },
        { name: '6 Strategies to Lower Your Tax Bill - Investopedia', link: 'https://www.investopedia.com/articles/personal-finance/032116/top-6-strategies-protect-your-income-taxes.asp' },
        { name: "How to Invest in Stocks: A Beginner's Guide - The Motley Fool", link: 'https://www.fool.com/the-knowledge-center/how-to-invest-in-stocks/' },
      ]
    }
  ];

  const categories = [
    { name: 'All', icon: BookOpen },
    { name: 'Presentations', icon: Presentation },
    { name: 'Age-Specific Resources', icon: Users },
    { name: 'Retirement Accounts', icon: Target },
    { name: 'Financial Education', icon: DollarSign },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.075,
        type: 'spring',
        stiffness: 100,
        damping: 10
      }
    })
  };

  const ResourceCard = ({ title, icon, content, index }) => (
    <motion.div
      variants={itemVariants}
      custom={index}
      className="bg-white rounded-xl border border-blue-300 shadow-lg p-6 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
    >
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 rounded-full text-blue-500 bg-blue-100 flex items-center justify-center mr-4">
          {icon}
        </div>
        <h3 className="text-xl font-bold">{title}</h3>
      </div>
      <div className="space-y-2">
        {content.map((item, itemIndex) => (
          <a 
            key={itemIndex}
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center py-2 px-4 bg-gray-50 rounded-lg border border-gray-300 shadow-md text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors hover:shadow-lg"
          >
            {item.name}
          </a>
        ))}
      </div>
    </motion.div>
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="max-w-7xl mx-auto p-8 space-y-10 font-roboto bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen"
    >
      <motion.div variants={itemVariants} custom={0}>
        <SectionTitle>Advisor Resources for Education Events</SectionTitle>
      </motion.div>

      <motion.div variants={itemVariants} custom={1} className="mb-8">
        <nav className="flex flex-wrap justify-center gap-4 bg-white p-6 rounded-2xl shadow-lg border border-blue-300">
          {categories.map((category) => (
            <motion.button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`flex items-center px-6 py-3 rounded-xl transition-all duration-300 ${
                selectedCategory === category.name
                  ? 'bg-gradient-to-bl from-sky-400 to-blue-800 text-white shadow-md'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <category.icon className={`w-5 h-5 mr-3 ${selectedCategory === category.name ? 'text-white' : 'text-blue-500'}`} />
              <span className="font-medium">{category.name}</span>
            </motion.button>
          ))}
        </nav>
      </motion.div>

      <motion.main variants={containerVariants}>
        {categories.map((category, categoryIndex) => (
          <motion.div
            key={category.name}
            variants={itemVariants}
            custom={categoryIndex + 2}
            className={selectedCategory === 'All' || selectedCategory === category.name ? '' : 'hidden'}
          >
            <div className="bg-white border border-gray-300 rounded-xl shadow-lg overflow-hidden mb-8">
              <div className="bg-gradient-to-bl from-sky-400 to-blue-800 p-4 shadow-sm">
                <h2 className="text-2xl font-light text-white flex items-center">
                  <category.icon className="h-6 w-6 mr-2" />
                  {category.name}
                </h2>
              </div>
              <div className="p-6">
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {resources
                    .filter(resource => category.name === 'All' || resource.category === category.name)
                    .map((resource, index) => (
                      <ResourceCard key={index} {...resource} index={index} />
                    ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.main>
    </motion.div>
  );
};

export default EducationalEvents;